import { useMemo, useCallback } from "react";

import { getFeatureFlag, EMPTY_STRING, roundAutoToDecimals } from "@bridgesplit/utils";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import { useMemoizedKeyMap } from "@bridgesplit/ui";

import {
    useRewardsWithMultipliersQuery,
    useUserPointsQuery,
    useUserReferralInfoQuery,
    usePointsTotalForRewardQuery,
    useUserReferralPointsQuery,
    usePortfolioRewardsQuery,
    useUserPointsRankQuery
} from "../reducers";
import {
    LoopscalePointsAction,
    PortfolioRewards,
    PointsRewardType,
    PortfolioRewardsFilter,
    UserPointsCalculationInfo,
    StrategyRewards,
    LedgerReward,
    VaultReward,
    RewardPointsPerSecond,
    LoanMultiplier,
    LoanMultiplierInfo,
    RewardsFilter,
    BreakdownActivity,
    PortfolioRewardsBreakdown,
    PointsReward
} from "../types";
import { BASE_PPS, BASE_REFERRAL_PPS } from "../constants";
import { useAbfTypesToUiConverter } from "../utils";
import { useLoopscaleWalletContext } from "../wallet";

export function useSkipPoints() {
    const {
        data: { userWalletAddress: activeWallet }
    } = useLoopscaleWalletContext();

    return useMemo(() => !activeWallet, [activeWallet]);
}

const DEFAULT_REWARDS_FILTER: RewardsFilter = { feVisible: true, activeOnly: true };

export function useRewards() {
    const { data: rewardsAndMultipliers, isLoading } = useRewardsWithMultipliersQuery(DEFAULT_REWARDS_FILTER);
    const rewards = rewardsAndMultipliers?.rewards;

    const rewardGetKey = useCallback((t: PointsReward) => t.reward, []);
    const rewardToInfo = useMemoizedKeyMap(rewards, rewardGetKey);

    function getRewardInfo(type: PointsRewardType) {
        return rewardToInfo?.get(type);
    }

    return { data: rewards, getRewardInfo, isLoading };
}

export function useUserReferral(options?: { skip?: boolean }) {
    const skip = useSkipPoints();
    const {
        data: { userWalletAddress }
    } = useLoopscaleWalletContext();
    const userId = userWalletAddress ?? skipToken;

    return useUserReferralInfoQuery(userId, {
        skip: skip || !!options?.skip
    });
}

export type UserPointsFunction = ReturnType<typeof useUserPoints>["getPointsAtTime"];
export function useUserPoints(options?: { skip?: number }) {
    // await for referral info or invite code endpoints before calling points to catch point creation endpoints
    const {
        data: { userWalletAddress }
    } = useLoopscaleWalletContext();
    const userId = userWalletAddress ?? skipToken;

    const {
        data: basePoints,
        isLoading,
        isUninitialized
    } = useUserPointsQuery(userId, { skip: !userId || !!options?.skip });

    const {
        data: referralPoints,
        isLoading: referralsLoading,
        isUninitialized: referralsUninitialized
    } = useUserReferralPointsQuery(userId, { skip: !userId || !!options?.skip });

    // Clear data when wallet address is undefined or queries are fetching to prevent showing stale data
    const pointsData: UserPointsCalculationInfo | undefined =
        userWalletAddress && basePoints && referralPoints ? { basePoints, referralPoints } : undefined;

    return {
        getPointsAtTime: (timestamp: number) => getPointsAtTime(pointsData, timestamp),
        isLoading: isUninitialized || isLoading || referralsLoading || referralsUninitialized
    };
}

export function usePortfolioRewards({
    portfolioRewardsFilter,
    skip = false
}: {
    portfolioRewardsFilter?: Partial<PortfolioRewardsFilter>;
    skip?: boolean;
}) {
    const {
        data: { userWalletAddress: activeWallet }
    } = useLoopscaleWalletContext();

    const filter: PortfolioRewardsFilter = {
        loanAddresses: portfolioRewardsFilter?.loanAddresses ?? [],
        strategyAddresses: portfolioRewardsFilter?.strategyAddresses ?? [],
        stakeAddresses: portfolioRewardsFilter?.stakeAddresses ?? [],
        isActive: portfolioRewardsFilter?.isActive ?? undefined
    };

    const {
        data: portfolioRewards,
        isLoading,
        isFetching
    } = usePortfolioRewardsQuery(filter, {
        skip: skip || !activeWallet
    });

    const portfolioData = useMemo(() => {
        // Don't return any data if we're fetching to prevent showing stale data
        if (isFetching || isLoading || !activeWallet || skip) return undefined;
        if (!portfolioRewards) {
            return {
                strategies: {},
                loans: {},
                vaultStakes: {},
                earnVaults: {},
                loops: {}
            };
        }
        return portfolioRewards;
    }, [isFetching, isLoading, activeWallet, skip, portfolioRewards]);

    // Consider the data as loading if either initial loading or refetching is happening
    return { data: portfolioData, isLoading: isLoading || isFetching };
}

function getPointsAtTime(userInfo: UserPointsCalculationInfo | undefined, now: number) {
    if (!userInfo?.basePoints || !userInfo?.referralPoints) {
        return undefined;
    }

    const { basePoints: rawBase, referralPoints: rawReferral } = userInfo;

    // Add null checks with default 0
    const basePPS = rawBase.pps ?? BASE_PPS;
    const referralPPS = rawReferral.pps ?? BASE_REFERRAL_PPS;

    const baseSecondsElapsed = Math.max(now - rawBase.timestamp, 0);
    const basePoints = rawBase.points + basePPS * baseSecondsElapsed;

    const referralSecondsElapsed = Math.max(now - rawReferral.timestamp, 0);
    const referralPoints = rawReferral.points + referralPPS * referralSecondsElapsed;

    // Beta Points
    const betaPoints = rawBase.betaPoints ?? 0;

    // Total Points
    const totalPoints = basePoints + referralPoints + betaPoints;

    return { totalPoints, referralPoints, betaPoints };
}

export function usePointsSumForReward(pointsReward: PointsRewardType) {
    const { getRewardInfo, isLoading: rewardLoading } = useRewards();
    const {
        data: { userWalletAddress: userId }
    } = useLoopscaleWalletContext();
    const payload = userId ? { pointsReward, userId } : skipToken;

    const { data: totalMultiplier, isLoading: multiplierLoading } = usePointsTotalForRewardQuery(payload);

    const totalPoints = useMemo(() => {
        const rewardInfo = getRewardInfo(pointsReward);
        if (!rewardInfo || !totalMultiplier) return 0;
        return rewardInfo.pointsPerSecond * totalMultiplier;
    }, [getRewardInfo, pointsReward, totalMultiplier]);

    return { totalPoints, isLoading: rewardLoading || multiplierLoading };
}

function useLoanMultipliers(skip?: boolean) {
    const { data: rewardsAndMultipliers, isLoading } = useRewardsWithMultipliersQuery(DEFAULT_REWARDS_FILTER, { skip });
    const multipliers = rewardsAndMultipliers?.multipliers;

    const loanMultiplier = useMemo(() => {
        if (!multipliers) {
            return undefined;
        }

        // Group multipliers by mint
        const loanMultiplier = multipliers.reduce<LoanMultiplier>((acc, multiplier) => {
            const { mint, name, reward, multiplier: value } = multiplier;

            if (!acc[mint]) {
                acc[mint] = {} as Record<LoopscalePointsAction, LoanMultiplierInfo>;
            }

            acc[mint][reward] = {
                name,
                multiplier: value
            };

            return acc;
        }, {});

        return loanMultiplier;
    }, [multipliers]);

    return { loanMultipliers: loanMultiplier, isLoading };
}
export function useLoanMultipliersUtil(skip?: boolean) {
    const { loanMultipliers, isLoading } = useLoanMultipliers(skip);

    return { loanMultipliers, isLoading };
}

export function useRewardsPointsPerSecond() {
    const { data: rewards, isLoading } = useRewards();

    const rewardPointsPerSecond = useMemo(() => {
        if (!rewards) return {} as RewardPointsPerSecond;

        const pointsPerSecondMap = rewards.reduce<Record<LoopscalePointsAction, number>>((acc, reward) => {
            const rewardValue = reward.reward as LoopscalePointsAction;
            if (Object.values(LoopscalePointsAction).includes(rewardValue)) {
                acc[rewardValue] = reward.pointsPerSecond;
            }
            return acc;
        }, {} as Record<LoopscalePointsAction, number>);

        const idleLendPps = pointsPerSecondMap[LoopscalePointsAction.IdleCap] || 1;

        return rewards.reduce<RewardPointsPerSecond>((acc, reward) => {
            const rewardValue = reward.reward as LoopscalePointsAction;
            if (Object.values(LoopscalePointsAction).includes(rewardValue)) {
                acc[rewardValue] = {
                    pointsPerSecond: reward.pointsPerSecond,
                    relativePointsPerSecond: roundAutoToDecimals(reward.pointsPerSecond / idleLendPps, 0),
                    rewardLabel: reward.feRewardLabel
                };
            }
            return acc;
        }, {} as RewardPointsPerSecond);
    }, [rewards]);

    return { rewardPointsPerSecond, isLoading };
}

function mergeVaultRewards(a: VaultReward, b: VaultReward): VaultReward {
    const baseA = a.baseReward;
    const baseB = b.baseReward;

    const combinedUsdValue = baseA.usdValue + baseB.usdValue;

    const multiplier =
        combinedUsdValue > 0
            ? (baseA.multiplier * baseA.usdValue + baseB.multiplier * baseB.usdValue) / combinedUsdValue
            : Math.max(baseA.multiplier, baseB.multiplier);
    return {
        vaultName: a.vaultName,
        vaultMetadata: a.vaultMetadata,
        vaultAddress: a.vaultAddress,
        stakeAddress: EMPTY_STRING, // Not applicable for an aggregated reward
        vaultPrincipalMint: a.vaultPrincipalMint,
        baseReward: {
            ...baseA,
            pointsPerSecond: baseA.pointsPerSecond + baseB.pointsPerSecond,
            lastUpdateValue: baseA.lastUpdateValue + baseB.lastUpdateValue,
            usdValue: combinedUsdValue,
            accruedTimeStamp: Math.min(baseA.accruedTimeStamp, baseB.accruedTimeStamp),
            isActive: baseA.isActive || baseB.isActive,
            endTime: baseA.endTime === null || baseB.endTime === null ? null : Math.max(baseA.endTime, baseB.endTime),
            multiplier
        }
    };
}

function generateVaultAddressToVaultReward(vaultStakes: Record<string, VaultReward>): Record<string, VaultReward> {
    const map = new Map<string, VaultReward>();

    for (const reward of Object.values(vaultStakes)) {
        const existing = map.get(reward.vaultAddress);
        map.set(reward.vaultAddress, existing ? mergeVaultRewards(existing, reward) : reward);
    }

    return Object.fromEntries(map);
}

type ActivePortfolioRewards = PortfolioRewards & {
    vaultAddressToVaultReward: Record<string, VaultReward>;
};

// Returns all portfolio rewards for active user positions. Used for daily points accrual calculations
export function useActivePortfolioRewards(skip?: boolean) {
    const { data: portfolioRewards, isLoading } = usePortfolioRewards({
        portfolioRewardsFilter: { isActive: true },
        skip
    });

    const activePortfolioRewards = useMemo<ActivePortfolioRewards | undefined>(() => {
        if (!portfolioRewards) return undefined;
        return {
            ...portfolioRewards,
            //Vault address to vault reward in an FE type used in portfolio rewards daily points display to show an aggregated reward for a vault address
            vaultAddressToVaultReward: generateVaultAddressToVaultReward(portfolioRewards.vaultStakes ?? {})
        };
    }, [portfolioRewards]);

    return { data: activePortfolioRewards, isLoading };
}

// Takes all active portfolio rewards and returns a breakdown
export function useRewardsBreakdown() {
    const { data: portfolioRewards, isLoading: portfolioRewardsLoading } = useHistoricalPortfolioRewards();
    const { rewardPointsPerSecond, isLoading: rewardPointsPerSecondLoading } = useRewardsPointsPerSecond();

    const { convertPortfolioRewards } = useAbfTypesToUiConverter();

    const data = convertPortfolioRewards(portfolioRewards, rewardPointsPerSecond);

    const isLoading = portfolioRewardsLoading || rewardPointsPerSecondLoading;

    // Check if portfolioRewards is undefined or empty (no strategies, loans, vaultStakes, or loops)
    const isEmpty =
        !portfolioRewards ||
        (Object.keys(portfolioRewards.strategies).length === 0 &&
            Object.keys(portfolioRewards.loans).length === 0 &&
            Object.keys(portfolioRewards.vaultStakes).length === 0 &&
            Object.keys(portfolioRewards.earnVaults ?? {}).length === 0 &&
            Object.keys(portfolioRewards.loops).length === 0);

    if ((isEmpty || !rewardPointsPerSecond) && !isLoading) {
        return { data: [], isLoading };
    }

    // Group idle lend rewards by vault name
    // Create a map of vault names to their aggregated rewards for faster lookup
    const groupedData = getSimplePortfolioBreakdown(data);

    return { data: isLoading ? undefined : groupedData, isLoading };
}
/**
 * Simplifies portfolio breakdown by aggregating rewards for the same vault
 * Vaults with the same name will have their rewards combined into a single entry
 * Active stakes are displayed individually, while inactive stakes are aggregated
 * @param data Array of portfolio rewards breakdown
 * @returns Simplified array with aggregated inactive vault rewards
 */
function getSimplePortfolioBreakdown(data: PortfolioRewardsBreakdown[]): PortfolioRewardsBreakdown[] {
    const inactiveVaultMap: Record<string, PortfolioRewardsBreakdown> = {};
    const nonAggregatedRewards: PortfolioRewardsBreakdown[] = [];

    const isVaultReward = (reward: PortfolioRewardsBreakdown): boolean =>
        reward.activity === BreakdownActivity.LendVault;

    // TODO: Makeshift isActive check since endTime still considered rewards active
    const isActive = (reward: PortfolioRewardsBreakdown): boolean => reward.pointsPerDay > 0;

    for (const reward of data) {
        if (isVaultReward(reward) && reward.vaultMetadata) {
            const vaultName = reward.vaultMetadata.name;

            if (isActive(reward)) {
                // Keep active vault stakes as individual rows
                nonAggregatedRewards.push(reward);
            } else {
                // Aggregate inactive vault stakes by vault name
                if (inactiveVaultMap[vaultName]) {
                    // Update existing inactive vault entry
                    const existing = inactiveVaultMap[vaultName];

                    // Get existing and new boost values, defaulting to 1 if undefined
                    const existingBoost = existing.boost || 1;
                    const currentRewardBoost = reward.boost || 1;

                    // For inactive vaults, use totalPoints as a weight factor
                    const existingRewardTotalPoints = existing.totalPoints || 0;
                    const currentRewardTotalPoints = reward.totalPoints || 0;
                    const totalPoints = existingRewardTotalPoints + currentRewardTotalPoints;

                    // Calculate weighted boost based on totalPoints
                    const weightedBoost =
                        totalPoints > 0
                            ? (existingBoost * existingRewardTotalPoints) / totalPoints +
                              (currentRewardBoost * currentRewardTotalPoints) / totalPoints
                            : Math.max(existingBoost, currentRewardBoost);

                    inactiveVaultMap[vaultName] = {
                        ...existing,
                        totalPoints,
                        boost: weightedBoost
                    };
                } else {
                    inactiveVaultMap[vaultName] = reward;
                }
            }
        } else {
            // Keep non-vault rewards as is
            nonAggregatedRewards.push(reward);
        }
    }

    // Combine non-aggregated rewards with aggregated inactive vault rewards
    return [...nonAggregatedRewards, ...Object.values(inactiveVaultMap)];
}

// Returns all portfolio rewards including non active positions
export function useHistoricalPortfolioRewards(skip?: boolean) {
    const portfolioRewards = usePortfolioRewards({ portfolioRewardsFilter: {}, skip });
    return portfolioRewards;
}

// Custom hook to get portfolio rewards by loan addresses. Used for loan cards
export function usePortfolioRewardsByLoans(loanAddresses?: string[]) {
    const filter = loanAddresses?.length
        ? {
              loanAddresses,
              strategyAddresses: [],
              stakeAddresses: [],
              isActive: true
          }
        : undefined;

    return usePortfolioRewards({ portfolioRewardsFilter: filter });
}

export function usePortfolioRewardsUtils() {
    const { data: portfolioRewards } = useActivePortfolioRewards();

    /**
     * Returns reward data for a specific loan
     * @param loanAddress The address of the loan to check
     * @returns The reward data for the loan, or undefined if not found
     */
    const getLoanRewards = (loanAddress: string | undefined): LedgerReward[] | undefined => {
        const loanRewards = loanAddress ? portfolioRewards?.loans?.[loanAddress] : undefined;
        return loanRewards;
    };

    const getLoopRewards = (loopAddress: string | undefined): LedgerReward[] | undefined => {
        const loopRewards = loopAddress ? portfolioRewards?.loops?.[loopAddress] : undefined;
        return loopRewards;
    };

    /**
     * Returns reward data for a specific strategy
     * @param strategyAddress The address of the strategy to check
     * @returns The reward data for the strategy, or undefined if not found
     */
    const getStrategyRewards = (strategyAddress: string | undefined): StrategyRewards | undefined => {
        const strategyReward = strategyAddress ? portfolioRewards?.strategies?.[strategyAddress] : undefined;
        return strategyReward;
    };

    /**
     * Returns reward data for a specific vault address.
     */
    const getAggregatedStakeRewards = (vaultAddress: string | undefined): VaultReward | undefined => {
        if (!vaultAddress) return undefined;
        const vaultAddressRewards = portfolioRewards?.vaultAddressToVaultReward?.[vaultAddress];
        return vaultAddressRewards;
    };

    return {
        getLoanRewards,
        getLoopRewards,
        getStrategyRewards,
        getAggregatedStakeRewards,
        isLoading: !portfolioRewards
    };
}

export interface UseRewardsPointsBreakdownDataProps {
    loopscalePointsAction: LoopscalePointsAction | null | undefined;
    identifier: string | undefined;
    collateralMint?: string | undefined;
}

export interface RewardMultiplierInfo {
    mint: string;
    multiplier: number;
}

export interface RewardsPointsBreakdownData {
    relativePointsPerSecond: number | undefined;
    rewardLabel: string | undefined;
    multiplierInfos: RewardMultiplierInfo[];
    totalMultiplier: number;
    isLoading: boolean;
}

/**
 * Data-only hook for rewards points breakdown.
 * Returns raw data without UI concerns (StatProps, JSX, etc.)
 */
export function useRewardsPointsBreakdown({
    loopscalePointsAction,
    identifier,
    collateralMint
}: UseRewardsPointsBreakdownDataProps): RewardsPointsBreakdownData {
    const { rewardPointsPerSecond, isLoading: isRelativePointsLoading } = useRewardsPointsPerSecond();
    const { loanMultipliers, isLoading: isLoadingLoanMultipliers } = useLoanMultipliersUtil();

    const isLoading = isRelativePointsLoading || isLoadingLoanMultipliers;

    const actionRewards = loopscalePointsAction ? rewardPointsPerSecond?.[loopscalePointsAction] : undefined;
    const relativePointsPerSecond = actionRewards?.relativePointsPerSecond;
    const rewardLabel = actionRewards?.rewardLabel;

    const multiplierInfos: RewardMultiplierInfo[] = [];

    if (identifier && loopscalePointsAction) {
        const idMultiplier = loanMultipliers?.[identifier]?.[loopscalePointsAction]?.multiplier;
        if (idMultiplier !== undefined) {
            multiplierInfos.push({ mint: identifier, multiplier: idMultiplier });
        }
    }

    if (collateralMint && loopscalePointsAction) {
        const colMultiplier = loanMultipliers?.[collateralMint]?.[loopscalePointsAction]?.multiplier;
        if (colMultiplier !== undefined) {
            multiplierInfos.push({ mint: collateralMint, multiplier: colMultiplier });
        }
    }

    const multipliers = multiplierInfos.map((info) => info.multiplier);
    const productOfMultipliers = multipliers.length > 0 ? multipliers.reduce((acc, curr) => acc * curr, 1) : 1;

    return {
        relativePointsPerSecond,
        rewardLabel,
        multiplierInfos,
        totalMultiplier: productOfMultipliers * (relativePointsPerSecond ?? 1),
        isLoading
    };
}

export function useUserPointsRank(timestamp?: number) {
    const { data: userPointsRank, isLoading } = useUserPointsRankQuery(undefined, {
        skip: !getFeatureFlag("leaderboard")
    });

    return { userPointsRank, isLoading };
}
